import React, { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { DatabaseIcon, ExclamationCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useBackupSummary } from '../../../hooks/useBackupSummary';
import { useBackups } from '../../../hooks/useBackups';
import BackupSummaryCards from './BackupSummaryCards';
import BackupTreeTable from './BackupTreeTable';

function Backups() {
  const auth = useAuth();
  const { backups, count, loading, error, refetch } = useBackups();
  const {
    summary,
    loading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useBackupSummary();

  const onRefresh = useCallback(() => {
    refetch();
    refetchSummary();
  }, [refetch, refetchSummary]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const renderContent = () => {
    // Full-page spinner on the **initial** load only. A refetch — whether from the Refresh button
    // or from the silent OIDC token renew that gives `apiFetch` a new identity — must not unmount
    // the table, or it would silently discard the reader's search text, status filter, page and
    // every expanded row. The Refresh button carries its own `isLoading` for that.
    if (loading && backups.length === 0) {
      return <CenteredSpinner />;
    }

    if (error && backups.length === 0) {
      return (
        <EmptyState
          titleText="Unable to load backups"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={onRefresh}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        {/* A refresh that fails leaves the previous inventory on screen — stale but real — and
            says so, rather than replacing it with an error page. */}
        {error && (
          <Alert
            variant="danger"
            isInline
            title="Could not refresh backups"
            className="pf-v6-u-mb-md"
            actionLinks={<AlertActionLink onClick={onRefresh}>Retry</AlertActionLink>}
          >
            {error.message} Showing the last successfully loaded inventory.
          </Alert>
        )}

        <BackupSummaryCards summary={summary} loading={summaryLoading} error={summaryError} />

        {/* The list is fetched in one call at the endpoint's maximum page size. If a tenant ever
            outgrows it, say so rather than silently showing a truncated inventory — that is also
            the signal to move this page to server-side paging. */}
        {count > backups.length && (
          <Alert
            variant="warning"
            isInline
            title="Not all backups are shown"
            className="pf-v6-u-mb-md"
          >
            Your organization has {count} backup entries, and this page can show {backups.length} at a time. Please
            contact support so we can raise the limit.
          </Alert>
        )}

        <BackupTreeTable
          subjects={backups}
          staleAfterHours={summary?.stale_after_hours ?? null}
          ariaLabel="Backups list"
          withToolbar
          emptyState={
            <EmptyState titleText="No backups found" icon={DatabaseIcon} variant="lg" headingLevel="h2">
              <EmptyStateBody>
                No backup inventory is currently attributed to your organization. If you expect backups here, contact
                support.
              </EmptyStateBody>
            </EmptyState>
          }
        />
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>Backups</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <DatabaseIcon />
              </Icon>
              Backups
            </Content>
            <Content component="p">Backups of your virtual machines, as reported by the backup server.</Content>
          </FlexItem>
          {/* Read-only resource: refreshing is the only action there is, and it stays mounted with
              isLoading rather than vanishing into the page spinner. */}
          <FlexItem>
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              onClick={onRefresh}
              isLoading={loading || summaryLoading}
              isDisabled={loading || summaryLoading}
            >
              Refresh
            </Button>
          </FlexItem>
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
    </>
  );
}

export { Backups };
